<template>
  <b-modal
    :id="modalId"
    v-model="showModal"
    title="Tài khoản trả góp"
    @ok="handleOk"
    @cancel="handleCancel"
    @hidden="handleHidden"
    ok-title="Lưu"
    cancel-title="Hủy"
    :ok-disabled="isSaving || loadingInstallments"
    size="md"
  >
    <div class="installment-account-modal">
      <p class="mb-3">
        Chọn tài khoản dịch vụ trả góp dùng cho kết nối gian hàng
        <strong>{{ shopName }}</strong>
        (đơn hàng trả góp từ sàn sẽ ghi nhận theo tài khoản này khi cấu hình).
      </p>

      <b-form-group label="Tài khoản trả góp" label-for="installment-select">
        <b-form-select
          id="installment-select"
          class="select-style"
          v-model="selectedInstallmentId"
          :options="installmentSelectOptions"
          :disabled="isSaving || loadError"
          size="sm"
        />
        <small class="form-text text-muted">
          Chọn &quot;Không sử dụng&quot; để bỏ liên kết tài khoản trả góp.
        </small>
      </b-form-group>

      <b-alert v-if="loadError" show variant="warning" class="mt-2">
        {{ loadError }}
      </b-alert>

      <b-alert v-if="error" show variant="danger" class="mt-2">
        {{ error }}
      </b-alert>

      <div v-if="loadingInstallments" class="text-center py-2">
        <b-spinner small class="mr-2" variant="primary" />
        <span class="text-muted">Đang tải danh sách...</span>
      </div>

      <div v-if="isSaving" class="text-center mt-3">
        <b-spinner variant="primary" small class="mr-2" />
        <span class="text-muted">Đang lưu...</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { getToastInstance } from '@/utils/toastHelper';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');
const installmentRepository = RepositoryFactory.get('installments');

export default {
  name: 'InstallmentAccountModal',
  props: {
    shopName: {
      type: String,
      default: '',
    },
    configId: {
      type: [String, Number],
      required: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    /** Hiện tại từ API: number | null */
    initialInstallmentAccountId: {
      type: [Number, String],
      default: null,
    },
    modalId: {
      type: String,
      default: 'installment-account-modal',
    },
  },
  data() {
    return {
      installments: [],
      selectedInstallmentId: null,
      loadingInstallments: false,
      loadError: null,
      isSaving: false,
      error: null,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
    installmentSelectOptions() {
      const first = [
        { value: null, text: '— Không sử dụng tài khoản trả góp —' },
      ];
      const rest = this.installments.map((row) => ({
        value: row.id,
        text: this.formatOptionText(row),
      }));
      const orphanId = this.initialInstallmentAccountId;
      if (
        orphanId != null &&
        !rest.some((o) => Number(o.value) === Number(orphanId))
      ) {
        rest.unshift({
          value: orphanId,
          text: `TK #${orphanId} (không tìm thấy trong danh sách)`,
        });
      }
      return [...first, ...rest];
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.openModal();
      }
    },
  },
  methods: {
    ...getToastInstance(),

    formatOptionText(row) {
      const name = row.name || '';
      const code = row.code ? ` (${row.code})` : '';
      return `${name}${code}`.trim() || `ID ${row.id}`;
    },

    async openModal() {
      this.error = null;
      this.loadError = null;
      this.selectedInstallmentId =
        this.initialInstallmentAccountId != null
          ? Number(this.initialInstallmentAccountId)
          : null;
      await this.fetchInstallments();
    },

    async fetchInstallments() {
      this.loadingInstallments = true;
      try {
        const raw = await installmentRepository.getAll({
          page: 1,
          pageSize: 2000,
          keyword: '',
        });
        let items = raw?.data;
        if (!Array.isArray(items)) {
          items = raw?.result || raw?.items;
        }
        if (!Array.isArray(items) && Array.isArray(raw)) {
          items = raw;
        }
        this.installments = Array.isArray(items) ? items : [];
        if (this.installments.length === 0) {
          this.loadError =
            'Không tải được danh sách tài khoản trả góp. Vui lòng thử lại sau hoặc kiểm tra quyền truy cập.';
        } else {
          this.loadError = null;
        }
      } catch (e) {
        console.error('Error loading installments:', e);
        this.loadError =
          e.response?.data?.message ||
          'Không tải được danh sách tài khoản trả góp.';
        this.installments = [];
      } finally {
        this.loadingInstallments = false;
      }
    },

    handleCancel() {
      this.showModal = false;
    },

    handleHidden() {
      this.error = null;
    },

    async handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      await this.save();
    },

    async save() {
      try {
        this.isSaving = true;
        this.error = null;

        const payload = {
          configId: Number(this.configId),
          installmentAccountId: this.selectedInstallmentId,
        };

        await platformRepository.saveConfig(payload);

        this.$emit('saved', {
          configId: this.configId,
          installmentAccountId: this.selectedInstallmentId,
        });
        this.showModal = false;
      } catch (error) {
        console.error('Error saving installment account:', error);
        this.error =
          error.response?.data?.message ||
          'Không thể lưu cấu hình. Vui lòng thử lại.';
        this.makeToastFailure(this.error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.installment-account-modal {
  strong {
    color: #3699ff;
  }

  /* Cùng class `select-style` (global); gỡ viền/halo xanh Bootstrap khi focus */
  :deep(.select-style.form-control:focus),
  :deep(.select-style.form-control:focus-visible) {
    border-color: #ced4da;
    box-shadow: none;
    outline: 0;
  }
}
</style>
